<template>
  <el-dialog
    :visible.sync="visible"
    title=""
    width="1720px"
    class="member-detail-modal"
    @close="handleClose">
    <template v-if="member">
      <div v-if="memberLoading" class="member-loading">
        <i class="el-icon-loading" />
      </div>
      <template v-else-if="effectiveMember">
        <div class="member-details">
          <main-image :src="$file('avatars', effectiveMember.avatar)" style="width: 220px; height: 220px" class="image" />
          <data-list :list="memberDataList" />
        </div>

      <div class="points-history-section">
        <h4 class="section-title">{{ $tc('form.change_history') }}</h4>
        <div v-if="historyLoading" class="history-loading">
          <i class="el-icon-loading" />
        </div>
        <div v-else-if="pointsHistory.length === 0" class="history-empty">
          {{ $tc('general.no_history') }}
        </div>
        <el-table v-else :data="pointsHistory" class="points-history-table" max-height="480">
          <el-table-column :label="$tc('form.points_history_date_of_change')" prop="createdAt" width="180">
            <template slot-scope="scope">
              {{ scope.row.createdAt | date('LLL') }}
            </template>
          </el-table-column>
          <el-table-column :label="$tc('form.points_history_points_added')" width="110" align="right">
            <template slot-scope="scope">
              <span :class="['history-change', { 'history-change--negative': scope.row.pointsChange < 0 }]">
                {{ scope.row.pointsChange > 0 ? '+' : '' }}{{ scope.row.pointsChange }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :label="$tc('form.points_history_points_change')" min-width="165" align="right">
            <template slot-scope="scope">
              {{ scope.row.prevPoints }} → {{ scope.row.newPoints }}
            </template>
          </el-table-column>
          <el-table-column :label="$tc('form.points_history_basis')" min-width="220">
            <template slot-scope="scope">
              <span class="basis-cell">
                <span v-if="getPlacementForBasis(scope.row) === 1" class="medal-icon medal-icon--gold" title="1. mesto">🥇</span>
                <span v-else-if="getPlacementForBasis(scope.row) === 2" class="medal-icon medal-icon--silver" title="2. mesto">🥈</span>
                <span v-else-if="getPlacementForBasis(scope.row) === 3" class="medal-icon medal-icon--bronze" title="3. mesto">🥉</span>
                {{ getBasis(scope.row) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :label="$tc('form.points_history_tournament_name')" min-width="180" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ getTournamentName(scope.row) }}
            </template>
          </el-table-column>
          <el-table-column :label="$tc('form.points_history_category')" min-width="320">
            <template slot-scope="scope">
              {{ getCategory(scope.row) }}
            </template>
          </el-table-column>
          <el-table-column :label="$tc('form.points_history_discipline')" min-width="160" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ getDiscipline(scope.row) }}
            </template>
          </el-table-column>
        </el-table>

        <el-button
          v-if="canAddPoints"
          type="primary"
          icon="el-icon-plus"
          size="small"
          round
          class="add-points-btn"
          @click="showAddPointsForm = true">
          {{ $tc('form.add_points') }}
        </el-button>

        <div v-if="showAddPointsForm" class="add-points-form">
          <el-input-number
            v-model="manualPoints"
            :min="-9999"
            :max="9999"
            size="small"
            controls-position="right"
            style="width: 120px; margin-right: 8px" />
          <el-button size="small" @click="cancelAddPoints">{{ $tc('general.cancel') }}</el-button>
          <el-button type="primary" size="small" :loading="addingPoints" @click="submitAddPoints">
            {{ $tc('general.save') }}
          </el-button>
        </div>
      </div>
      </template>
    </template>
  </el-dialog>
</template>

<script>
import axios from 'axios'
import errorHandler from '@/common/errorHandler.js'

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },

    member: {
      type: Object,
      default: null
    },

    canAddPoints: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      displayedMember: null,
      pointsHistory: [],
      historyLoading: false,
      memberLoading: false,
      showAddPointsForm: false,
      manualPoints: 10,
      addingPoints: false
    }
  },

  computed: {
    visible: {
      get () {
        return this.value
      },
      set (v) {
        this.$emit('input', v)
      }
    },

    effectiveMember () {
      return this.displayedMember || this.member
    },

    memberDataList () {
      if (!this.effectiveMember) return []
      const points = this.effectiveMember.earnedPoints != null ? this.effectiveMember.earnedPoints : 0
      const m = this.effectiveMember
      const fullName = `${m.firstName || ''} ${m.lastName || ''}`.trim()
      return [
        { label: this.$tc('form.full_name'), text: fullName },
        { label: this.$tc('form.date_of_birth'), text: this.$options.filters.date(m.born) },
        { label: this.$tc('form.gender'), text: this.$options.filters.sex(m.isMale) },
        { label: this.$tc('form.belt'), text: this.$options.filters.belt(m.belt) },
        {
          label: this.$tc('general.points'),
          html: `<span class="member-points-tag-inline">${points}</span>`
        }
      ]
    }
  },

  watch: {
    'value' (visible) {
      if (visible && this.member) {
        this.displayedMember = this.member
        this.loadMemberAndHistory()
        this.showAddPointsForm = false
        this.manualPoints = 10
      } else {
        this.displayedMember = null
      }
    }
  },

  methods: {
    async loadMemberAndHistory () {
      if (!this.member?.id) return
      this.memberLoading = true
      this.historyLoading = true
      const [memberResult, historyResult] = await Promise.allSettled([
        axios.get(`model/Members/${this.member.id}`),
        axios.get('model/MemberPointsHistory', {
          params: {
            q: { memberId: this.member.id },
            by: ['createdAt:DESC']
          }
        })
      ])

      try {
        if (memberResult.status === 'fulfilled') {
          this.displayedMember = memberResult.value?.data || this.member
          if (this.displayedMember) {
            this.$emit('update', this.displayedMember)
          }
        } else {
          this.displayedMember = this.member
        }

        if (historyResult.status === 'fulfilled') {
          this.pointsHistory = Array.isArray(historyResult.value?.data) ? historyResult.value.data : []
        } else {
          this.pointsHistory = []
        }

        if (memberResult.status === 'rejected' && historyResult.status === 'rejected') {
          errorHandler(memberResult.reason)
        }
      } finally {
        this.memberLoading = false
        this.historyLoading = false
      }
    },

    async loadPointsHistory () {
      if (!this.member?.id) return
      this.historyLoading = true
      try {
        const response = await axios.get('model/MemberPointsHistory', {
          params: {
            q: { memberId: this.member.id },
            by: ['createdAt:DESC']
          }
        })
        this.pointsHistory = Array.isArray(response.data) ? response.data : []
      } catch (e) {
        errorHandler(e)
        this.pointsHistory = []
      } finally {
        this.historyLoading = false
      }
    },

    cancelAddPoints () {
      this.showAddPointsForm = false
      this.manualPoints = 10
    },

    async submitAddPoints () {
      const points = parseInt(this.manualPoints, 10)
      if (Number.isNaN(points) || points === 0) return
      this.addingPoints = true
      try {
        const result = await this.$store.dispatch('members/action', {
          id: this.member.id,
          action: 'addPoints',
          payload: { points }
        })
        const updated = result?.item
        if (updated) {
          this.displayedMember = updated
          this.$emit('update', updated)
        }
        await this.loadPointsHistory()
        this.$message.success(this.$tc('messages.success_updated_member') || 'Poeni su dodati.')
        this.showAddPointsForm = false
        this.manualPoints = 10
      } catch (e) {
        errorHandler(e)
      } finally {
        this.addingPoints = false
      }
    },

    handleClose () {
      this.$emit('input', false)
    },

    getReasonParts (entry) {
      const raw = (entry.reason && entry.reason.trim()) ? entry.reason.trim() : ''
      return raw ? raw.split(/\s*,\s*/) : []
    },

    isManualEntry (entry) {
      const parts = this.getReasonParts(entry)
      return parts.length === 1 && parts[0] === 'Ručno dodato poena'
    },

    getBasis (entry) {
      const parts = this.getReasonParts(entry)
      if (this.isManualEntry(entry)) {
        return this.getHistoryByLabel(entry)
      }
      if (parts.length >= 1 && parts[0] === 'Učešće') {
        return 'Učešće'
      }
      if (parts.length >= 1) {
        return parts[0]
      }
      return this.getHistoryByLabel(entry)
    },

    getTournamentName (entry) {
      if (this.isManualEntry(entry)) return ''
      const parts = this.getReasonParts(entry)
      return (parts.length >= 2 && parts[1]) ? parts[1] : ''
    },

    getCategory (entry) {
      if (this.isManualEntry(entry)) return ''
      const parts = this.getReasonParts(entry)
      return (parts.length >= 3 && parts[2]) ? parts[2] : ''
    },

    getDiscipline (entry) {
      if (this.isManualEntry(entry)) return ''
      const parts = this.getReasonParts(entry)
      return (parts.length >= 4 && parts[3]) ? parts[3] : ''
    },

    getHistoryByLabel (entry) {
      if (entry.user && (entry.user.email || entry.user.name)) {
        const name = entry.user.name || entry.user.email
        return this.$t('form.points_history_by_user', { name }) || `Od strane: ${name}`
      }
      return this.$t('form.points_history_by_system') || 'Sistem turnira'
    },

    getPlacementForBasis (entry) {
      const parts = this.getReasonParts(entry)
      const first = (parts.length >= 1 && parts[0]) ? parts[0].trim() : ''
      if (/^1\.\s*mesto$/i.test(first) || /^1st\s+place$/i.test(first) || /^prvo\s+mesto$/i.test(first)) return 1
      if (/^2\.\s*mesto$/i.test(first) || /^2nd\s+place$/i.test(first) || /^drugo\s+mesto$/i.test(first)) return 2
      if (/^3\.\s*mesto$/i.test(first) || /^3rd\s+place$/i.test(first) || /^treće\s+mesto$/i.test(first) || /^trece\s+mesto$/i.test(first)) return 3
      return null
    }
  }
}
</script>

<style lang="scss" scoped>
.member-detail-modal {
  ::v-deep .el-dialog__body {
    padding: 28px 32px 32px;
    min-height: 640px;
  }
}

.member-loading {
  padding: 48px 0;
  text-align: center;
  font-size: 24px;
  color: #909399;
}

.member-details {
  display: flex;
  gap: 24px;
  align-items: flex-start;

  .image {
    flex-shrink: 0;
  }

  ::v-deep .data-list {
    flex: 1;
    min-width: 0;
  }
}

.points-history-section {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

.section-title {
  margin: 0 0 14px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.history-loading,
.history-empty {
  padding: 16px 0;
  color: #909399;
  font-size: 14px;
}

.points-history-table {
  margin-bottom: 16px;

  ::v-deep .el-table th {
    white-space: nowrap;
  }

  .history-change {
    font-weight: 600;

    &.history-change--negative {
      color: #f56c6c;
    }
  }

  .history-change:not(.history-change--negative) {
    color: #67c23a;
  }

  .basis-cell {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .medal-icon {
    flex-shrink: 0;
    font-style: normal;
    line-height: 1;
  }
}

.add-points-btn {
  margin-bottom: 8px;
}

.add-points-form {
  margin-top: 12px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 4px;
  display: flex;
  align-items: center;
}

.member-points-tag-inline {
  display: inline-block;
  padding: 2px 8px;
  font-size: 13px;
  font-weight: 600;
  background-color: #000;
  color: #fff;
  border-radius: 2px;
}
</style>
